import React, {
  useCallback,
  useLayoutEffect,
  useState,
  useEffect,
  useMemo,
} from "react";
import { useLocation, useParams } from "react-router-dom";
import { merge } from "lodash";
import {
  ActionIcon,
  Box,
  Button,
  Center,
  Checkbox,
  ColorInput,
  Divider,
  Group,
  NumberInput,
  Select,
  Stack,
  Switch,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { DatePicker, TimeInput } from "@mantine/dates";
import { skipToken } from "@reduxjs/toolkit/query";

import type { Crossbar } from "crossbar/types/crossbar.model";
import type { Event } from "event/types/event.model";
import type { FoodMenu } from "click-and-collect/types/foodMenu.model";
import type { Game } from "quiz/types/game.model";
import type { Wall } from "wall/types/wall.model";
// import type { Workforce } from "workforce/admin/workforce.model";

import {
  EVENT_CONTACT_FORM_CHECKBOX_LINK,
  EVENT_CONTACT_FORM_CHECKBOX_MESSAGE,
  EVENT_CONTACT_FORM_VALIDATION_BUTTON,
  EVENT_CONTACT_FORM_WELCOME,
  EVENT_LANDING_MSG,
} from "_constants";
import {
  ADMIN_ORGANIZATION,
  EVENT_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS,
  EVENT_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES,
  ORGANIZATIONS,
} from "_roles";
import { AccessRights } from "common/admin/auth/AccessRights";
import Collapsable from "common/admin/ui/Collapsable";
import FileInput from "common/admin/ui/FileInput";
import QueryWrapper from "common/components/QueryWrapper";
import {
  useGetEventQuery,
  useUpdateEventMutation,
  useListPogsQuery,
} from "event/api/event.api";
import { CrossbarEventPresenter } from "crossbar/components/CrossbarEventPresenter";
import EventBreadcrumbs from "event/admin/components/EventBreadcrumbs";
import EventToolbar from "event/admin/components/EventToolbar";
import PlayerSelect from "event/admin/components/PlayerSelect";
import {
  EventFormProvider,
  useEventForm,
} from "event/context/event.form.context";
import { useListCrossbarsQuery } from "crossbar/api/crossbar.api";
import { useListFoodMenusQuery } from "click-and-collect/api/foodMenu.api";
import { useListGamesQuery } from "quiz/api/games.endpoints";
import { useListWallsQuery } from "wall/api/walls.endpoints";
import { useListWorkforcesQuery } from "workforce/admin/workforce.api";
import { useListBingoGamesQuery } from "bingo/api/bingo-game.api";
import { defaultEvent } from "event/event.defaults";
import { useOrganization } from "common/admin/auth/hooks";
import { ColorsFormInput } from "../../../common/admin/forms/ColorsFormInput";
import ImagesForm from "../../../common/admin/forms/ImagesForm";
import { BingoGame } from "bingo/types/bingo-game.model";
import { useListHTMLPagesQuery } from "htmlpage/api/html-page.api";
import { HtmlPage } from "htmlpage/types/html-page.model";
import { PronosticV2 } from "pronosticsv2/types/pronosticv2.model";
import { useListPronosticV2sQuery } from "pronosticsv2/api/pronosticv2.api";
import { CirclePlus, Copy, Trash } from "tabler-icons-react";

const goal_names = ["Jannick Green", "Mikkel Lovkvist"];

void goal_names;

const EventView = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const creation = !eventId;
  const [userOrganization] = useOrganization();

  const form = useEventForm({
    initialValues: {
      ...defaultEvent,
      organization: userOrganization || "",
    },
    validate: {
      opponent: {
        name: (value) => {
          if (!value) return "Le nom de l'adversaire est requis.";
        },
      },
      team_name: (value) => {
        if (!value) return "Le nom de votre équipe est requis.";
      },
      kickoff_date(value) {
        if (creation && new Date(value) < new Date())
          return "Le coup d'envoi doit être dans le futur.";
      },
      // workforce_snapshot(value: Workforce | null) {
      //   if (!value) return "Un effectif doit être sélectionné.";
      // },
      footer: {
        footerBackground: (value) => !value && "Footer background is required.",
        // footerImage: (value) => !value && "Footer image is required.",
      },
    },
  });
  const { setValues } = form;
  const organization = form.values.organization;

  // Set organization from user's organization if not already set
  useEffect(() => {
    if (userOrganization && !organization) {
      form.setFieldValue("organization", userOrganization);
    }
  }, [userOrganization, organization, form]);

  const queryFiltersByOrganization = (
    organization: string | undefined,
  ): Record<string, string> | undefined => {
    if (
      organization &&
      organization !== "" &&
      organization !== "136" &&
      organization !== ADMIN_ORGANIZATION
    ) {
      return { organization };
    }
    return;
  };

  const listCrossbarsQuery = useListCrossbarsQuery(
    queryFiltersByOrganization(organization),
  );

  const crossbars = useMemo(
    () =>
      (listCrossbarsQuery.data || []).filter(
        (crossbar) => crossbar.creationTime && crossbar.name,
      ),
    [listCrossbarsQuery.data],
  );

  const getEventQuery = useGetEventQuery(eventId ?? skipToken);

  const listFoodMenusQuery = useListFoodMenusQuery(
    queryFiltersByOrganization(organization),
  );

  const foodMenus = useMemo(
    () =>
      (listFoodMenusQuery.data || []).filter(
        (foodMenu) => foodMenu.creationTime && foodMenu.label,
      ),
    [listFoodMenusQuery.data],
  );

  const listGamesQuery = useListGamesQuery(
    queryFiltersByOrganization(organization),
  );

  const games = useMemo(
    () =>
      (listGamesQuery.data || []).filter(
        (game) =>
          game.creationTime &&
          game.name &&
          game.quizId &&
          game.quizSnapshot?.creationTime,
      ),
    [listGamesQuery.data],
  );

  const listWallsQuery = useListWallsQuery(
    queryFiltersByOrganization(organization),
  );

  const listWorkforcesQuery = useListWorkforcesQuery(
    queryFiltersByOrganization(organization),
  );

  const listBingoGamesQuery = useListBingoGamesQuery(
    queryFiltersByOrganization(organization)
      ? { ...queryFiltersByOrganization(organization), status: "NOT_STARTED" }
      : { status: "NOT_STARTED" },
  );

  const listHtmlPagesQuery = useListHTMLPagesQuery(
    queryFiltersByOrganization(organization),
  );

  const listPronosticV2sQuery = useListPronosticV2sQuery();

  useLayoutEffect(() => {
    const data = getEventQuery.data;
    if (data) {
      setValues(
        merge({}, defaultEvent, data, {
          kickoff_date: new Date(data.kickoff_date).toISOString(),
        }),
      );
    }
  }, [getEventQuery, setValues]);

  const location = useLocation();
  const isNew = location.pathname.endsWith("/new");

  useLayoutEffect(() => {
    const state = location.state as { from?: Event } | undefined;
    if (state?.from) {
      state.from.man_of_the_match_display = false;
      state.from.pog_candidates = defaultEvent.pog_candidates;
      state.from.results = defaultEvent.results;

      setValues(state.from);
      window.history.replaceState(null, "");
    }
  }, [location, setValues]);

  const [updateEvent] = useUpdateEventMutation();
  const pogs = Object.fromEntries(
    (useListPogsQuery(eventId!).data || []).map((pog: any) => [
      pog.playerId,
      pog.votes,
    ]),
  );

  const [endingVote, setEndingVote] = useState<boolean>(false);

  const filterCrossbarsByOrganization =
    (organization: string | undefined) =>
    (crossbar: Crossbar): boolean => {
      if (organization !== "136") {
        return crossbar.access.organization === organization;
      }
      return true;
    };

  const filterCrossbars = useCallback(
    (crossbars: Crossbar[]) => {
      const crossbarId = form.getInputProps("crossbar_id").value;
      const matchingCrossbar: Crossbar[] = crossbars.filter(
        (crossbar) => crossbar.id === crossbarId,
      );
      let filteredCrossbars = crossbars;
      if (organization && organization !== "136") {
        filteredCrossbars = crossbars.filter(
          filterCrossbarsByOrganization(
            form.getInputProps("organization").value,
          ),
        );
      }
      const results = [...filteredCrossbars, ...matchingCrossbar];
      return results
        .sort(function (a, b) {
          if (a.creationTime > b.creationTime) return -1;
          if (a.creationTime < b.creationTime) return +1;
          return 0;
        })
        .sort(function (a, b) {
          if (a.creationTime > b.creationTime) return -1;
          if (a.creationTime < b.creationTime) return +1;
          return 0;
        })
        .map((crossbar) => ({
          label: `${crossbar.name} --- ${crossbar.access.organization}`,
          value: crossbar.id,
        }));
    },
    [form, organization],
  );

  const filterFoodMenuByOrganization =
    (organization: string | undefined) =>
    (foodMenu: FoodMenu): boolean => {
      if (organization !== "136") {
        return foodMenu.organization === organization;
      }
      return true;
    };

  const filterFoodMenus = useCallback(
    (foodMenus: FoodMenu[]) => {
      const foodMenuId = form.getInputProps("foodMenu").value;
      const matchingFoodMenu: FoodMenu[] = foodMenus.filter(
        (foodMenu) => foodMenu.id === foodMenuId,
      );
      let filteredFoodMenus = foodMenus;
      if (organization && organization !== "136") {
        filteredFoodMenus = foodMenus.filter(
          filterFoodMenuByOrganization(
            form.getInputProps("organization").value,
          ),
        );
      }
      const results = [...filteredFoodMenus, ...matchingFoodMenu];
      return results
        .sort(function (a, b) {
          if (a.creationTime > b.creationTime) return -1;
          if (a.creationTime < b.creationTime) return +1;
          return 0;
        })
        .sort(function (a, b) {
          if (a.creationTime > b.creationTime) return -1;
          if (a.creationTime < b.creationTime) return +1;
          return 0;
        })
        .map((foodMenu) => ({
          label: `${foodMenu.label} --- ${foodMenu.organization}`,
          value: foodMenu.id,
        }));
    },
    [form, organization],
  );

  const filterGamesByOrganization =
    (organization: string | undefined) =>
    (game: Game): boolean => {
      if (organization !== "136") {
        return (
          game.state.step === "CREATED" &&
          game.quizSnapshot?.organization === organization
        );
      }
      return game.state.step === "CREATED";
    };

  const filterGames = useCallback(
    (games: Game[]) => {
      const eventQuizId = form.getInputProps("quiz_id").value;
      const matchingGame: Game[] = games.filter(
        (game) => game.quizId === eventQuizId,
      );
      let filteredGames = games;
      if (organization && organization !== "136") {
        filteredGames = games.filter(
          filterGamesByOrganization(form.getInputProps("organization").value),
        );
      }
      const results = [...filteredGames, ...matchingGame];
      return results
        .sort(function (a, b) {
          if (a.quizSnapshot.creationTime > b.quizSnapshot.creationTime)
            return -1;
          if (a.quizSnapshot.creationTime < b.quizSnapshot.creationTime)
            return +1;
          return 0;
        })
        .sort(function (a, b) {
          if (a.creationTime > b.creationTime) return -1;
          if (a.creationTime < b.creationTime) return +1;
          return 0;
        })
        .map((game) => ({
          label: `${game.quizSnapshot?.name} --- ${game.name}`,
          value: game.id,
        }));
    },
    [form, organization],
  );

  const filterWallByOrganization =
    (organization: string | undefined) =>
    (wall: Wall): boolean => {
      if (organization) {
        return wall.organization === organization;
      }
      return true;
    };

  const filterDiaporamas = useCallback(
    (walls: Wall[]) => {
      let filteredWalls = walls;
      if (organization && organization !== "136") {
        filteredWalls = walls.filter(filterWallByOrganization(organization));
      }
      return filteredWalls.map((wall) => ({
        label: wall.name || `Diaporama ${wall.id}`,
        value: wall.id,
      }));
    },
    [organization],
  );

  const formatBingoGamesForSelect = useCallback(
    (bingoGames: BingoGame[]) =>
      [...bingoGames] // Create a shallow copy to avoid mutating the original array
        .sort(
          (a, b) =>
            new Date(b.creationTime).getTime() -
            new Date(a.creationTime).getTime(),
        )
        .map((bingoGame) => ({
          label: `${bingoGame.bingoTemplateSnapshot?.name} --- ${bingoGame.name}`,
          value: bingoGame.id,
        })),
    [],
  );

  const formatHtmlPagesForSelect = useCallback(
    (htmlPages: HtmlPage[]) =>
      htmlPages.map((htmlPage) => ({
        label: `${htmlPage.id} --- ${htmlPage.name}`,
        value: htmlPage.id,
      })),
    [],
  );

  const formatPronosticsV2ForSelect = useCallback(
    (pronosticsV2: PronosticV2[]) =>
      [...pronosticsV2]
        .filter(
          (pronosticV2) =>
            pronosticV2.owner.L1 === organization &&
            pronosticV2.status !== "archived",
        )
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )
        .map((pronosticV2) => ({
          label: `${pronosticV2.id} --- ${pronosticV2.name}`,
          value: pronosticV2.id,
        })),
    [organization],
  );

  const handleAddFooter = () => {
    form.insertListItem("footer", {
      footerBackground: "",
      footerImage: "",
    });
  };

  const handleCopyFooter = (index: number) => {
    const footerToCopy = form?.values?.footer?.[index];
    if (footerToCopy) {
      form.insertListItem("footer", { ...footerToCopy });
    }
  };

  const handleDeleteFooter = (index: number) => {
    form.removeListItem("footer", index);
  };

  const isLastFooterValid = () => {
    const lastFooter = form.values.footer?.[form.values.footer.length - 1];
    return !!(lastFooter?.footerBackground && lastFooter?.footerImage);
  };

  void isLastFooterValid; //suppressing error, to inspect why it was there

  const eventTeamName = form.values.team_name || "Équipe locale";
  const eventOpponentName = form.values.opponent.name || "Adversaire";

  const listCrossbarsQueryFiltered = useMemo(
    () => ({ ...listCrossbarsQuery, data: crossbars }),
    [listCrossbarsQuery, crossbars],
  );

  const listFoodMenusQueryFiltered = useMemo(
    () => ({ ...listFoodMenusQuery, data: foodMenus }),
    [listFoodMenusQuery, foodMenus],
  );

  const listGamesQueryFiltered = useMemo(
    () => ({ ...listGamesQuery, data: games }),
    [listGamesQuery, games],
  );

  return (
    <QueryWrapper
      queries={[
        getEventQuery,
        listCrossbarsQueryFiltered,
        listFoodMenusQueryFiltered,
        listGamesQueryFiltered,
        listWallsQuery,
        listWorkforcesQuery,
        listBingoGamesQuery,
        listHtmlPagesQuery,
        listPronosticV2sQuery,
      ]}
    >
      {([
        event,
        crossbars,
        foodMenus,
        games,
        walls,
        workforces,
        bingoGames,
        htmlPages,
        pronosticsV2,
      ]) => {
        return (
          <EventFormProvider form={form}>
            <div
              className="sticky top-0 z-50 pb-4"
              style={{ backgroundColor: "#E9ECEF" }}
            >
              <Title order={3} align={"center"} m={"xs"}>
                {creation
                  ? "New Event"
                  : event.name && event.name.trim()
                    ? event.name
                    : event.opponent.name
                      ? `${eventTeamName} / ${event.opponent.name}`
                      : `Event ${event.id}`}
              </Title>
              <EventBreadcrumbs event={event ?? "new"} />
              <EventToolbar />
            </div>

            <Stack>
              <Collapsable title="Parameters">
                <Stack>
                  <AccessRights
                    authorizedOrganizations={
                      EVENT_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS
                    }
                    authorizedRoles={
                      EVENT_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES
                    }
                  >
                    <Select
                      sx={{ flex: 1 }}
                      label={"Organization"}
                      data={ORGANIZATIONS}
                      {...form.getInputProps("organization")}
                    />
                  </AccessRights>
                  <AccessRights
                    authorizedOrganizations={ORGANIZATIONS.map(
                      (org) => org.value,
                    ).filter((org) => org !== ADMIN_ORGANIZATION)}
                    authorizedRoles={["Content"]}
                  >
                    <TextInput
                      sx={{ flex: 1 }}
                      label={"Organization"}
                      value={organization}
                      readOnly
                    />
                  </AccessRights>

                  <TextInput
                    sx={{ flex: 1 }}
                    label={"Name"}
                    //disabled={!creation && event.team_name}
                    {...form.getInputProps("name")}
                  />

                  <TextInput
                    required
                    sx={{ flex: 1 }}
                    label={"Host"}
                    //disabled={!creation && event.team_name}
                    {...form.getInputProps("team_name")}
                  />
                  <TextInput
                    required
                    sx={{ flex: 1 }}
                    label={"Opponent Name"}
                    //disabled={!creation && event.opponent?.name}
                    {...form.getInputProps("opponent.name")}
                  />
                  <FileInput
                    label={"Opponent Logo"}
                    {...form.getInputProps("opponent.logo")}
                  />
                  <Group grow>
                    <DatePicker
                      required
                      label={"Event Date"}
                      //disabled={!creation}
                      {...form.getInputProps("kickoff_date")}
                      value={new Date(form.values.kickoff_date)}
                      onChange={(value: string | null) => {
                        if (!value) return;

                        const newDate = new Date(value);
                        const eventDate = new Date(form.values.kickoff_date);
                        form.setFieldValue(
                          "kickoff_date",
                          new Date(
                            eventDate.setFullYear(
                              newDate.getFullYear(),
                              newDate.getMonth(),
                              newDate.getDate(),
                            ),
                          ).toISOString(),
                        );
                      }}
                    />
                    <TimeInput
                      required
                      label={"Event Time"}
                      //disabled={!creation}
                      {...form.getInputProps("kickoff_date")}
                      value={new Date(form.values.kickoff_date)}
                      onChange={(value: string | null) => {
                        if (!value) return;

                        const newDate = new Date(value);
                        const eventDate = new Date(form.values.kickoff_date);
                        form.setFieldValue(
                          "kickoff_date",
                          new Date(
                            eventDate.setHours(
                              newDate.getHours(),
                              newDate.getMinutes(),
                            ),
                          ).toISOString(),
                        );
                      }}
                    />
                  </Group>
                  {/* <Switch
                    className="pb-3"
                    checked={form.values.extra_fields}
                    label="Enable Extra Fields"
                    onLabel="Yes"
                    offLabel="No"
                    onChange={(event) => {
                      const value = event.currentTarget.checked;
                      form.setFieldValue("extra_fields", value);
                    }}
                    {...form.getInputProps("extra_fields")}
                  /> */}
                </Stack>
              </Collapsable>
              <Collapsable title="Extra Fields">
                <Stack spacing="md">
                  <Group className="grid grid-cols-3 grid-rows-1">
                    <Checkbox
                      label="Prénom"
                      checked={true}
                      disabled={true}
                      readOnly={true}
                    />
                    <Checkbox
                      label="Nom"
                      checked={true}
                      disabled={true}
                      readOnly={true}
                    />
                    <Checkbox
                      label="Téléphone"
                      checked={true}
                      disabled={true}
                      readOnly={true}
                    />
                  </Group>
                  <Group className="grid grid-cols-3 grid-rows-1">
                    <Checkbox
                      label="Email"
                      checked={form.values.extra_fields_object?.email === true}
                      onChange={(event) => {
                        const value = event.currentTarget.checked;
                        const currentObject =
                          form.values.extra_fields_object || {};
                        form.setFieldValue("extra_fields_object", {
                          ...currentObject,
                          email: value,
                        });
                      }}
                    />
                    <Checkbox
                      label="Room"
                      checked={form.values.extra_fields_object?.room === true}
                      onChange={(event) => {
                        const value = event.currentTarget.checked;
                        const currentObject =
                          form.values.extra_fields_object || {};
                        form.setFieldValue("extra_fields_object", {
                          ...currentObject,
                          room: value,
                        });
                      }}
                    />
                    <Checkbox
                      label="Enterprise"
                      checked={
                        form.values.extra_fields_object?.enterprise === true
                      }
                      onChange={(event) => {
                        const value = event.currentTarget.checked;
                        const currentObject =
                          form.values.extra_fields_object || {};
                        form.setFieldValue("extra_fields_object", {
                          ...currentObject,
                          enterprise: value,
                        });
                      }}
                    />
                  </Group>
                  <Checkbox
                    label="Terms and Conditions"
                    checked={
                      form.values.extra_fields_object?.termsandconditions ===
                      true
                    }
                    onChange={(event) => {
                      const value = event.currentTarget.checked;
                      const currentObject =
                        form.values.extra_fields_object || {};
                      form.setFieldValue("extra_fields_object", {
                        ...currentObject,
                        termsandconditions: value,
                      });
                    }}
                  />

                  <div className="text-lg text-semibold">Location Fields</div>
                  <div className="grid grid-cols-3 gap-4">
                    {[1, 2, 3].map((index) => (
                      <Stack key={index} spacing="xs">
                        <Checkbox
                          label={`Filed ${index}`}
                          checked={
                            form.values.extra_fields_object?.[
                              `location${index}_enabled`
                            ] === true
                          }
                          onChange={(event) => {
                            const value = event.currentTarget.checked;
                            const currentObject =
                              form.values.extra_fields_object || {};
                            form.setFieldValue("extra_fields_object", {
                              ...currentObject,
                              [`location${index}_enabled`]: value,
                            });
                          }}
                        />
                        <Group className="grid grid-cols-2">
                          <TextInput
                            placeholder={`Field ${index} name`}
                            value={
                              (form.values.extra_fields_object?.[
                                `location${index}_name`
                              ] as string) || ""
                            }
                            onChange={(event) => {
                              const value = event.currentTarget.value;
                              const currentObject =
                                form.values.extra_fields_object || {};
                              form.setFieldValue("extra_fields_object", {
                                ...currentObject,
                                [`location${index}_name`]: value,
                              });
                            }}
                            className="ml-6"
                          />
                        </Group>
                      </Stack>
                    ))}
                  </div>
                </Stack>
              </Collapsable>

              <Collapsable title="Colors" defaultOpened={isNew ? true : false}>
                <Box>
                  <Title order={4}>Header</Title>
                  <ColorsFormInput
                    form={form}
                    fields={[
                      ["headerBackground", "Event Banner Background"],
                      ["headerEventText", "Header Event Text"],
                      ["headerEventDateText", "Header Event Date Text"],
                      ["registerFormLabels", "Register form labels"],
                      [
                        "registerFormSubmitButton",
                        "Register form submit button",
                      ],
                      ["registerFormSubmitButtonBorder", "Button border"],
                      ["registerFormLegalCheckBox", "Checkbox CGU"],
                      ["registeredEventHomePageText", "Home page Event"],
                      ["navigationMenuBackground", "Background navigation"],
                      ["navigationMenuText", "Text navigation"],
                      ["inputBorderColor", "Input border color"],
                      ["eventBackgroundColor", "Event background color"],
                    ]}
                  />
                </Box>
              </Collapsable>
              <Collapsable title="Header" defaultOpened={isNew ? true : false}>
                <Switch
                  className="pb-3"
                  checked={form.values.header?.enabled}
                  label="Enable Header"
                  onLabel="Yes"
                  offLabel="No"
                  onChange={(event) => {
                    const value = event.currentTarget.checked;
                    form.setFieldValue("header.enabled", value);
                  }}
                  {...form.getInputProps("header.enabled")}
                />
                <Textarea
                  className="pb-3"
                  minRows={15}
                  label="Header HTML"
                  {...form.getInputProps("header.html")}
                />
                <Select
                  className="pb-3"
                  label="Navigation Menu Type"
                  placeholder="Select navigation menu type"
                  data={[
                    { value: "default", label: "Default" },
                    { value: "transparent", label: "Transparent" },
                  ]}
                  clearable={true}
                  {...form.getInputProps("navigation.navigationType")}
                />
              </Collapsable>
              <Collapsable title="Images" defaultOpened={isNew ? true : false}>
                <ImagesForm
                  form={form}
                  fields={[
                    ["eventDesktopBackground", "Background (screen)"],
                    ["eventMobileBackground", "Background (mobile)"],
                    ["teamLogo", "Team Logo"],
                    ["navigationMenuLogo", "Navigation Menu Logo"],
                  ]}
                />
                <Select
                  label="Background Position"
                  placeholder="Select background position"
                  data={[
                    // { value: "inherit", label: "inherit" },
                    // { value: "initial", label: "initial" },
                    // { value: "center", label: "center" },
                    // { value: "right", label: "right" },
                    // { value: "left", label: "left" },
                    // { value: "bottom", label: "bottom" },
                    // { value: "top", label: "top" },
                    {
                      value: "center bottom",
                      label: "horizontal: center, vertical: bottom",
                    },
                    {
                      value: "center center",
                      label: "horizontal: center, vertical: center",
                    },
                    {
                      value: "center top",
                      label: "horizontal: center, vertical: top",
                    },
                    {
                      value: "right bottom",
                      label: "horizontal: right, vertical: bottom",
                    },
                    {
                      value: "right center",
                      label: "horizontal: right, vertical: center",
                    },
                    {
                      value: "right top",
                      label: "horizontal: right, vertical: top",
                    },
                    {
                      value: "left bottom",
                      label: "horizontal: left, vertical: bottom",
                    },
                    {
                      value: "left center",
                      label: "horizontal: left, vertical: center",
                    },
                    {
                      value: "left top",
                      label: "horizontal: left, vertical: top",
                    },
                  ]}
                  {...form.getInputProps("images.backGroundPosition")}
                />
              </Collapsable>
              <Collapsable title="Files" defaultOpened={isNew ? true : false}>
                <FileInput
                  label={"Link to terms and conditions"}
                  isPDF={true}
                  {...form.getInputProps("legalUrl")}
                />
              </Collapsable>
              <Collapsable
                title="Font Family"
                defaultOpened={isNew ? true : false}
              >
                <Select
                  className="pb-3"
                  label={"Font"}
                  placeholder={"Choose a font"}
                  data={[
                    { value: "poppins", label: "Poppins" },
                    { value: "virage", label: "Virage" },
                    { value: "marfa", label: "Marfa" },
                    { value: "audi", label: "Audi" },
                  ]}
                  clearable={true}
                  {...form.getInputProps("font")}
                />
              </Collapsable>
              <Collapsable title="Texts" defaultOpened={isNew ? true : false}>
                <Textarea
                  className="pb-3"
                  key="register-form-welcome-msg"
                  label="Introduction"
                  name="registerFormWelcomeMsg"
                  placeholder={EVENT_CONTACT_FORM_WELCOME}
                  {...form.getInputProps("labels.register_form_welcome_msg")}
                />
                <Textarea
                  className="pb-3"
                  key="register-form-checkbox-msg"
                  label="Terms Acceptance Message"
                  name="registerFormCheckboxMsg"
                  placeholder={EVENT_CONTACT_FORM_CHECKBOX_MESSAGE}
                  {...form.getInputProps("labels.register_form_checkbox_msg")}
                />
                <Textarea
                  className="pb-3"
                  key="register-form-checkbox-link"
                  label="Terms acceptance document"
                  name="registerFormCheckboxLink"
                  placeholder={EVENT_CONTACT_FORM_CHECKBOX_LINK}
                  {...form.getInputProps("labels.register_form_checkbox_link")}
                />
                <Textarea
                  className="pb-3"
                  key="register-form-thanks-msg"
                  label="Register form Thanks Message"
                  name="registerFormThanksMsg"
                  placeholder={EVENT_CONTACT_FORM_WELCOME}
                  {...form.getInputProps("labels.register_form_thanks_msg")}
                />
                <Textarea
                  className="pb-3"
                  key="registered-welcome-msg"
                  label="Welcome Message"
                  name="registeredWelcomeMsg"
                  placeholder={EVENT_LANDING_MSG}
                  {...form.getInputProps("labels.registered_welcome_msg")}
                />
                <Textarea
                  className="pb-3"
                  key="register-form-validation-button"
                  label="Button Label"
                  name="registerFormValidationButton"
                  placeholder={EVENT_CONTACT_FORM_VALIDATION_BUTTON}
                  {...form.getInputProps(
                    "labels.register_form_validation_button",
                  )}
                />
                <TextInput
                  className="pb-3"
                  key="login-title"
                  label="Login Title Size"
                  name="loginTitleSize"
                  placeholder="eg: 1.5rem, 5px etc"
                  {...form.getInputProps("labels.login_title_size")}
                />
                <TextInput
                  className="pb-3"
                  required
                  sx={{ flex: 1 }}
                  label={"Navigation Title"}
                  {...form.getInputProps("navigation.title")}
                />
              </Collapsable>
              <Collapsable title="Footers" defaultOpened={isNew ? true : false}>
                <Stack>
                  {form.values.footer?.map((footer, index) => (
                    <div key={index}>
                      <Group position="apart">
                        <Title order={4}>Footer #{index + 1}</Title>
                        <Group spacing="xs">
                          <ActionIcon
                            onClick={() => handleCopyFooter(index)}
                            disabled={
                              !footer.footerBackground || !footer.footerImage
                            }
                          >
                            <Copy size={20} color="blue" />
                          </ActionIcon>
                          <ActionIcon onClick={() => handleDeleteFooter(index)}>
                            <Trash color="red" size={20} />
                          </ActionIcon>
                        </Group>
                      </Group>
                      <Group className="grid grid-cols-2">
                        <ColorInput
                          required
                          label="Footer Background"
                          placeholder="Enter background color/URL"
                          {...form.getInputProps(
                            `footer.${index}.footerBackground`,
                          )}
                        />
                        <FileInput
                          label="Footer Image"
                          {...form.getInputProps(`footer.${index}.footerImage`)}
                        />
                      </Group>
                    </div>
                  ))}
                  <Group position="center" align="center">
                    <Button
                      onClick={handleAddFooter}
                      // disabled={!isLastFooterValid()}
                      variant="subtle"
                      leftIcon={<CirclePlus />}
                    >
                      Add Footer
                    </Button>
                  </Group>
                </Stack>
              </Collapsable>

              <Collapsable title="Quiz" defaultOpened={isNew ? true : false}>
                <Switch
                  className="pb-3"
                  checked={form.values.quiz_display}
                  label="Show Quiz"
                  onLabel="Yes"
                  offLabel="No"
                  onChange={(event) => {
                    const value = event.currentTarget.checked;
                    form.setFieldValue("quiz_display", value);
                  }}
                  {...form.getInputProps("quiz_display")}
                />
                <TextInput
                  className="pb-3"
                  required
                  sx={{ flex: 1 }}
                  label={"Navigation menu label"}
                  {...form.getInputProps("navigation.quiz")}
                />
                <Group>
                  <Select
                    placeholder={"Choose Quiz"}
                    data={filterGames(games)}
                    clearable={true}
                    {...form.getInputProps("quiz_id")}
                    sx={{ width: "70%" }}
                  />
                  {form.values.quiz_id && (
                    <Button
                      component="a"
                      href={`/admin/quizzes/${games.find((g) => g.id === form.values.quiz_id)?.quizId}/games/${form.values.quiz_id}`}
                      variant="outline"
                    >
                      Edit
                    </Button>
                  )}
                </Group>
              </Collapsable>
              <Collapsable
                title="Slideshow"
                defaultOpened={isNew ? true : false}
              >
                <Switch
                  className="pb-3"
                  checked={form.values.selfies_display}
                  label="Show Slideshow"
                  onLabel="Yes"
                  offLabel="No"
                  onChange={(event) => {
                    const value = event.currentTarget.checked;
                    form.setFieldValue("selfies_display", value);
                  }}
                  {...form.getInputProps("selfies_display")}
                />
                <TextInput
                  className="pb-3"
                  required
                  sx={{ flex: 1 }}
                  label={"Navigation Menu Label"}
                  {...form.getInputProps("navigation.selfies")}
                />
                <Select
                  className="pb-3"
                  label={"Choose Slideshow"}
                  placeholder={"Choose Slideshow"}
                  data={filterDiaporamas(walls)}
                  clearable={true}
                  {...form.getInputProps("wall_id")}
                />
                <Textarea
                  key="wall-waiting-msg"
                  label="Default Text"
                  name="wallWaitingMsg"
                  placeholder="L'envoi des messages n'est pas encore ouvert"
                  {...form.getInputProps("wall_waiting_msg")}
                />
              </Collapsable>
              <Collapsable
                title="Slideshow 2"
                defaultOpened={isNew ? true : false}
              >
                <Switch
                  className="pb-3"
                  checked={form.values.questions_display}
                  label="Show Questions"
                  onLabel="Yes"
                  offLabel="No"
                  onChange={(event) => {
                    const value = event.currentTarget.checked;
                    form.setFieldValue("questions_display", value);
                  }}
                  {...form.getInputProps("questions_display")}
                />
                <TextInput
                  className="pb-3"
                  required
                  sx={{ flex: 1 }}
                  label={"Navigation Menu Label"}
                  {...form.getInputProps("navigation.questions")}
                />
                <Select
                  className="pb-3"
                  label={"Choose Slideshow"}
                  placeholder={"Choose Slideshow"}
                  data={filterDiaporamas(walls)}
                  clearable={true}
                  {...form.getInputProps("wall_questions_id")}
                />
                <Textarea
                  key="wall-questions-waiting-msg"
                  label="Default Waiting Message"
                  name="wallQuestionsWaitingMsg"
                  placeholder="L'envoi des questions est terminé. Profitez du match !"
                  {...form.getInputProps("wall_questions_waiting_msg")}
                />
              </Collapsable>

              <Collapsable
                title="Man of the Match"
                defaultOpened={isNew ? true : false}
              >
                <Switch
                  className="pb-3"
                  checked={form.values.man_of_the_match_display}
                  label="Show Man of the Match"
                  onLabel="Yes"
                  offLabel="No"
                  onChange={(event) => {
                    const value = event.currentTarget.checked;
                    form.setFieldValue("man_of_the_match_display", value);
                  }}
                  {...form.getInputProps("man_of_the_match_display")}
                />
                <TextInput
                  className="pb-3"
                  required
                  sx={{ flex: 1 }}
                  label={"Navigation Menu Label"}
                  {...form.getInputProps("navigation.man_of_match")}
                />
                <Select
                  required
                  label={"Effectif du match"}
                  placeholder={"Choix de l'effectif"}
                  //disabled={!creation && event.workforce_snapshot}
                  data={workforces.map((workforce) => ({
                    label: workforce.name,
                    value: workforce.id,
                  }))}
                  {...form.getInputProps("workforce_snapshot")}
                  value={form.values.workforce_snapshot?.id}
                  onChange={(value: string | null) => {
                    for (const workforce of workforces) {
                      if (workforce.id === value) {
                        form.setFieldValue("workforce_snapshot", workforce);
                        break;
                      }
                    }
                  }}
                />
                <Stack>
                  <Group grow>
                    {form.values.pog_candidates.map((pog_candidate, index) => (
                      <Stack key={index}>
                        <PlayerSelect
                          disabled={
                            event?.pog_candidates.filter(
                              (candidate) => candidate != null,
                            ).length > 0
                          }
                          label={
                            <Title className="pb-3" order={4}>
                              Player {index + 1}
                            </Title>
                          }
                          placeholder={"Select a player"}
                          players={form.values.workforce_snapshot?.players}
                          value={pog_candidate?.toString()}
                          onChange={(value: string | null) =>
                            value &&
                            form.setFieldValue(
                              `pog_candidates.${index}`,
                              Number(value),
                            )
                          }
                        />
                        Votes : {pogs[index] || 0}
                      </Stack>
                    ))}
                  </Group>
                  {eventId &&
                    ((!endingVote && (
                      <Button
                        className="bg-button-admin"
                        disabled={event.pog_vote_ended}
                        onClick={() => setEndingVote(true)}
                      >
                        Stop Voting
                      </Button>
                    )) || (
                      <Stack>
                        <Center
                          style={{
                            fontWeight: "bold",
                          }}
                        >
                          Are you sure you want to stop the voting?
                        </Center>
                        <Group grow>
                          <Button
                            className="bg-button-admin disabled:opacity-75"
                            onClick={() => {
                              updateEvent({
                                id: eventId,
                                ...form.values,
                                pog_vote_ended: true,
                              });
                              setEndingVote(false);
                            }}
                          >
                            Yes
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => setEndingVote(false)}
                          >
                            No
                          </Button>
                        </Group>
                      </Stack>
                    ))}
                </Stack>
              </Collapsable>

              <AccessRights
                authorizedOrganizations={EVENT_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS.concat(
                  "PSG",
                )}
                authorizedRoles={EVENT_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES.concat(
                  "Content",
                )}
              >
                <Collapsable
                  title="Pronostics V1"
                  defaultOpened={isNew ? true : false}
                >
                  <Switch
                    className="pb-3"
                    checked={form.values.pronostics_display}
                    label="Show Pronostics"
                    onLabel="Yes"
                    offLabel="No"
                    onChange={(event) => {
                      const value = event.currentTarget.checked;
                      form.setFieldValue("pronostics_display", value);
                    }}
                    {...form.getInputProps("pronostics_display")}
                  />
                  <TextInput
                    className="pb-3"
                    required
                    sx={{ flex: 1 }}
                    label={"Navigation Menu Label"}
                    {...form.getInputProps("navigation.pronostics")}
                  />
                  <Stack spacing={"xl"}>
                    <Stack spacing={"xs"}>
                      <Divider
                        label={<Title order={3}>End of Match Score</Title>}
                      />
                      <Group grow>
                        <Stack>
                          <NumberInput
                            min={0}
                            label={<Title order={4}>{eventTeamName}</Title>}
                            {...form.getInputProps(
                              "results.match_end_score.own",
                            )}
                          />
                        </Stack>
                        <Stack>
                          <NumberInput
                            min={0}
                            label={<Title order={4}>{eventOpponentName}</Title>}
                            {...form.getInputProps(
                              "results.match_end_score.opponent",
                            )}
                          />
                        </Stack>
                      </Group>
                    </Stack>
                    <Divider />
                    <Group grow>
                      <Title order={3}>Top Scorer</Title>
                      <PlayerSelect
                        placeholder={"Select a Player"}
                        players={form.values.workforce_snapshot?.players}
                        value={form.values.results.best_scorer?.toString()}
                        onChange={(value: string | null) =>
                          value &&
                          form.setFieldValue(
                            "results.best_scorer",
                            Number(value),
                          )
                        }
                      />
                    </Group>
                    <Group grow>
                      <Title order={3}>Last Scorer</Title>
                      <PlayerSelect
                        placeholder={"Select a Player"}
                        players={form.values.workforce_snapshot?.players}
                        value={form.values.results.last_scorer?.toString()}
                        onChange={(value: string | null) =>
                          value &&
                          form.setFieldValue(
                            "results.last_scorer",
                            Number(value),
                          )
                        }
                      />
                    </Group>
                    <Group grow>
                      <Title order={3}>Goals stopped</Title>

                      <Group className="grid grid-cols-2 grid-rows-3">
                        <TextInput
                          className="pb-3"
                          sx={{ flex: 1 }}
                          label={"Goalkeeper 1"}
                          {...form.getInputProps(
                            "goalkeepers.goalkeeper1_name",
                          )}
                        />
                        <TextInput
                          className="pb-3"
                          sx={{ flex: 1 }}
                          label={"Goalkeeper 2"}
                          {...form.getInputProps(
                            "goalkeepers.goalkeeper2_name",
                          )}
                        />
                        <FileInput
                          label="Photo"
                          {...form.getInputProps(
                            "goalkeepers.goalkeeper1_image",
                          )}
                        />
                        <FileInput
                          label="Photo"
                          {...form.getInputProps(
                            "goalkeepers.goalkeeper2_image",
                          )}
                        />

                        {form.values.results.goals_stopped.map((_, index) => (
                          <NumberInput
                            key={index}
                            label={"Stops"}
                            min={0}
                            {...form.getInputProps(
                              `results.goals_stopped.${index}`,
                            )}
                          />
                        ))}
                      </Group>
                    </Group>
                  </Stack>
                </Collapsable>
              </AccessRights>
              <Collapsable title="Menu" defaultOpened={isNew ? true : false}>
                <Switch
                  className="pb-3"
                  checked={form.values.food_menu_display}
                  label="Show Food Menu"
                  onLabel="Yes"
                  offLabel="No"
                  onChange={(event) => {
                    const value = event.currentTarget.checked;
                    form.setFieldValue("food_menu_display", value);
                  }}
                  {...form.getInputProps("food_menu_display")}
                />
                <TextInput
                  className="pb-3"
                  required
                  sx={{ flex: 1 }}
                  label={"Navigation Menu Label"}
                  {...form.getInputProps("navigation.click_and_collect")}
                />
                <Select
                  className="pb-3"
                  label={"Choose Food Menu"}
                  placeholder={"Choose Food Menu"}
                  data={filterFoodMenus(foodMenus)}
                  clearable={true}
                  {...form.getInputProps("food_menu")}
                />
              </Collapsable>
              <Collapsable title="Iframe" defaultOpened={isNew ? true : false}>
                <Switch
                  className="pb-3"
                  checked={form.values.food_menu_v2_display}
                  label="Show Iframe"
                  onLabel="Yes"
                  offLabel="No"
                  onChange={(event) => {
                    const value = event.currentTarget.checked;
                    form.setFieldValue("food_menu_v2_display", value);
                  }}
                  {...form.getInputProps("food_menu_v2_display")}
                />
                <TextInput
                  className="pb-3"
                  required
                  sx={{ flex: 1 }}
                  label={"Navigation Menu Label"}
                  {...form.getInputProps("navigation.food_menu_v2")}
                />
                <TextInput
                  className="pb-3"
                  required
                  sx={{ flex: 1 }}
                  label={"Iframe URL"}
                  {...form.getInputProps("food_menu_v2_url")}
                />
              </Collapsable>
              <Collapsable
                title="Crossbar challenge"
                defaultOpened={isNew ? true : false}
              >
                <Switch
                  className="pb-3"
                  checked={form.values.crossbar_display}
                  label="Show crossbar challenge"
                  onLabel="Yes"
                  offLabel="No"
                  onChange={(event) => {
                    const value = event.currentTarget.checked;
                    form.setFieldValue("crossbar_display", value);
                  }}
                  {...form.getInputProps("crossbar_display")}
                />
                <TextInput
                  className="pb-3"
                  required
                  sx={{ flex: 1 }}
                  label={"Navigation Menu Label"}
                  {...form.getInputProps("navigation.crossbar")}
                />
                <Select
                  placeholder={"Choose Crossbar"}
                  data={filterCrossbars(crossbars)}
                  clearable={true}
                  {...form.getInputProps("crossbar_id")}
                />

                {organization &&
                form.values.crossbar_id &&
                form.values.crossbar_event_id &&
                crossbars &&
                crossbars.length > 0 &&
                crossbars.filter(
                  (crossbar) => crossbar.id === form.values.crossbar_id,
                ).length > 0 ? (
                  <CrossbarEventPresenter
                    crossbar={
                      crossbars.filter(
                        (crossbar) => crossbar.id === form.values.crossbar_id,
                      )[0]
                    }
                    crossbarEventId={form.values.crossbar_event_id}
                    eventForm={form}
                    organization={organization}
                  />
                ) : (
                  <></>
                )}

                {organization &&
                form.values.crossbar_id &&
                !form.values.crossbar_event_id &&
                crossbars &&
                crossbars.length > 0 &&
                crossbars.filter(
                  (crossbar) => crossbar.id === form.values.crossbar_id,
                ).length > 0 ? (
                  <CrossbarEventPresenter
                    crossbar={
                      crossbars.filter(
                        (crossbar) => crossbar.id === form.values.crossbar_id,
                      )[0]
                    }
                    eventForm={form}
                    organization={organization}
                  />
                ) : (
                  <></>
                )}
              </Collapsable>
              <Collapsable title="Bingo" defaultOpened={isNew ? true : false}>
                <Switch
                  className="pb-3"
                  checked={form.values.bingo_game_display}
                  label="Show Bingo"
                  onLabel="Yes"
                  offLabel="No"
                  onChange={(event) => {
                    const value = event.currentTarget.checked;
                    form.setFieldValue("bingo_game_display", value);
                  }}
                  {...form.getInputProps("bingo_game_display")}
                />
                <TextInput
                  className="pb-3"
                  required
                  sx={{ flex: 1 }}
                  label={"Navigation Menu Label"}
                  {...form.getInputProps("navigation.bingo")}
                />
                <Group>
                  <Select
                    placeholder={"Select Bingo Game"}
                    data={formatBingoGamesForSelect(bingoGames)} // Using the function to transform the data
                    clearable={true}
                    {...form.getInputProps("bingo_game_id")}
                    sx={{ width: "70%" }}
                  />
                  {form.values.bingo_game_id ? (
                    <Button
                      component="a"
                      href={`/admin/bingo/bingogames/${form.values.bingo_game_id}`}
                      variant="outline"
                    >
                      Edit
                    </Button>
                  ) : (
                    <Button
                      component="a"
                      href="/admin/bingo/bingogames/new"
                      variant="outline"
                    >
                      Create
                    </Button>
                  )}
                </Group>
              </Collapsable>
              <Collapsable
                title="Html Page"
                defaultOpened={isNew ? true : false}
              >
                <Switch
                  className="pb-3"
                  checked={form.values.html_page_display}
                  label="Show Html Page"
                  onLabel="Yes"
                  offLabel="No"
                  onChange={(event) => {
                    const value = event.currentTarget.checked;
                    form.setFieldValue("html_page_display", value);
                  }}
                  {...form.getInputProps("html_page_display")}
                />
                <TextInput
                  className="pb-3"
                  required
                  sx={{ flex: 1 }}
                  label={"Navigation Menu Label"}
                  {...form.getInputProps("navigation.html_page")}
                />
                <Group>
                  <Select
                    placeholder={"Select Html Page"}
                    data={formatHtmlPagesForSelect(htmlPages)} // Using the function to transform the data
                    clearable={true}
                    {...form.getInputProps("html_page_id")}
                    sx={{ width: "70%" }}
                  />
                  {form.values.html_page_id ? (
                    <Button
                      component="a"
                      href={`/admin/htmlpageadmin/${form.values.html_page_id}`}
                      variant="outline"
                    >
                      Edit
                    </Button>
                  ) : (
                    <Button
                      component="a"
                      href="/admin/htmlpageadmin/new"
                      variant="outline"
                    >
                      Create
                    </Button>
                  )}
                </Group>
              </Collapsable>
              <Collapsable
                title="Html Page No2"
                defaultOpened={isNew ? true : false}
              >
                <Switch
                  className="pb-3"
                  checked={form.values.html_page_two_display}
                  label="Show Html Page No2"
                  onLabel="Yes"
                  offLabel="No"
                  onChange={(event) => {
                    const value = event.currentTarget.checked;
                    form.setFieldValue("html_page_two_display", value);
                  }}
                  {...form.getInputProps("html_page_two_display")}
                />
                <TextInput
                  className="pb-3"
                  required
                  sx={{ flex: 1 }}
                  label={"Navigation Menu Label"}
                  {...form.getInputProps("navigation.html_page_two")}
                />
                <Group>
                  <Select
                    placeholder={"Select Html Page"}
                    data={formatHtmlPagesForSelect(htmlPages)} // Using the function to transform the data
                    clearable={true}
                    sx={{ width: "70%" }}
                    {...form.getInputProps("html_page_two_id")}
                  />
                  {form.values.html_page_two_id ? (
                    <Button
                      component="a"
                      href={`/admin/htmlpageadmin/${form.values.html_page_two_id}`}
                      variant="outline"
                    >
                      Edit
                    </Button>
                  ) : (
                    <Button
                      component="a"
                      href="/admin/htmlpageadmin/new"
                      variant="outline"
                    >
                      Create
                    </Button>
                  )}
                </Group>
              </Collapsable>
              <Collapsable
                title="Pronostics V2"
                defaultOpened={isNew ? true : false}
              >
                <Switch
                  className="pb-3"
                  checked={form.values.pronostics_v2_display}
                  label="Show Pronostics V2"
                  onLabel="Yes"
                  offLabel="No"
                  onChange={(event) => {
                    const value = event.currentTarget.checked;
                    form.setFieldValue("pronostics_v2_display", value);
                  }}
                  {...form.getInputProps("pronostics_v2_display")}
                />
                <TextInput
                  className="pb-3"
                  required
                  sx={{ flex: 1 }}
                  label={"Navigation Menu Label"}
                  {...form.getInputProps("navigation.pronostics_v2")}
                />
                <Group>
                  <Select
                    placeholder={"Select Pronostics V2"}
                    data={formatPronosticsV2ForSelect(pronosticsV2)}
                    clearable={true}
                    sx={{ width: "70%" }}
                    {...form.getInputProps("pronostics_v2_id")}
                  />
                  {form.values.pronostics_v2_id ? (
                    <Button
                      component="a"
                      href={`/admin/pronosticsv2/${form.values.pronostics_v2_id}`}
                      variant="outline"
                    >
                      Edit
                    </Button>
                  ) : (
                    <Button
                      component="a"
                      href="/admin/pronosticsv2/new"
                      variant="outline"
                    >
                      Create
                    </Button>
                  )}
                </Group>
              </Collapsable>
            </Stack>
          </EventFormProvider>
        );
      }}
    </QueryWrapper>
  );
};

export { EventView };
